import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight } from "lucide-react";

const FAQ: React.FC = () => {
  const [expandedFAQ, setExpandedFAQ] = useState<number>(-1);

  const faqs = [
    {
      q: "What is the structure and duration of the Cambridge Research Programmes?",
      a: "Each programme runs for 12 days and follows a hybrid delivery model. The structure includes: Days 1-2 for research foundations and academic communication, Days 3-5 for professor-led subject teaching (with in-person teaching on Day 5), Days 6-7 as a weekend break, Days 8-11 for project development and supervision, and Day 12 for final presentations and academic evaluation.",
    },
    {
      q: "Who are the instructors and academic leads?",
      a: "Each programme is designed and academically supervised by a University of Cambridge Professor. Daily in-person instruction is delivered by Cambridge PhD female researchers who are selected based on subject expertise, research background, and experience working with school-age learners. The Professor provides live online lectures, feedback sessions, and final evaluation.",
    },
    {
      q: "What subjects are available in the programme?",
      a: "The programme offers three specialized streams: Entrepreneurship (Cambridge Judge Business School), Artificial Intelligence (AI) covering machine learning and ethics, and Medicine focusing on biomedical and clinical research. Each stream is designed and supervised by Cambridge Professors with relevant expertise.",
    },
    {
      q: "What certification and recognition will I receive?",
      a: "All participants receive a Certificate of Attendance signed by the supervising Cambridge Professor (with academic title included). The certificate is co-signed by UniHive's Training Director (a retired Cambridge professor). Additionally, the top 50% of students in each programme receive recommendation letters based on academic performance, engagement, and final presentation quality.",
    },
    {
      q: "Who is eligible to participate in these programmes?",
      a: "The programmes are designed for high-achieving high-school students, typically aged 15-18, who demonstrate strong academic interest and motivation in the relevant subject area (Entrepreneurship, AI, or Medicine). Students should have a strong academic background and be prepared to engage in university-level research thinking and methodologies.",
    },
  ];

  return (
    <motion.section
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      viewport={{ once: true }}
      className="py-16 bg-white"
    >
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-gray-600 text-lg">
            Get answers to common questions about our research programmes at
            Cambridge.
          </p>
        </motion.div>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.05 }}
              viewport={{ once: true }}
              className="bg-gray-50 rounded-lg overflow-hidden"
            >
              <button
                onClick={() =>
                  setExpandedFAQ(expandedFAQ === index ? -1 : index)
                }
                className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-100 transition-colors"
              >
                <span className="font-semibold text-gray-900">{faq.q}</span>
                <ArrowRight
                  className={`h-5 w-5 text-gray-500 transform transition-transform ${
                    expandedFAQ === index ? "rotate-90" : ""
                  }`}
                />
              </button>
              <AnimatePresence>
                {expandedFAQ === index && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="px-6 pb-4"
                  >
                    <p className="text-gray-600 leading-relaxed">{faq.a}</p>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>
      </div>
    </motion.section>
  );
};

export default FAQ;
